import { Form } from 'antd'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ReactSelect } from 'components/ui/Select/ReactSelect'
import { Divider, FormItem, Input } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { NOT_LATIN_CYRILLIC_HYPHEN } from 'shared/constants/regExp'
import { DATE_FORMAT_DEFAULT } from 'shared/constants/values'
import { BaseOption } from 'shared/types/types'

import {
  citizenshipOptions,
  countriesWithMultipleDocuments,
  documentsTypeOptions,
  identificationCardLabels,
} from '../../../data'
import { CompanyDetailsViewModel } from '../../../stores/models/CompanyDetailsViewModel'
import { ProfileViewModel } from '../../../stores/models/ProfileViewModel'
import { Citizenship, DocumentType, UserModuleKeys } from '../../../types'
import { IdentificationForm } from './PassportForm/IdentificationForm'
import { PassportForm } from './PassportForm/PassportForm'
import { fioPlaceholders } from './PassportForm/requisites'

type PassportStepProps = {
  companyDetails: CompanyDetailsViewModel
  profile: ProfileViewModel
  isEdit?: boolean
}

export const PassportStep = observer(({ companyDetails, profile, isEdit = false }: PassportStepProps) => {
  const [form] = Form.useForm()
  const { t } = useTranslation()

  const getKey = (key: string) => `${UserModuleKeys.passportStep}.${key}`
  const getDataKey = (key: string) => `${UserModuleKeys.data}.${key}`
  const isCountryWithMultipleDocuments = countriesWithMultipleDocuments.some(
    (country) => country === companyDetails.selectedCitizenship.value,
  )

  const isMiddleNameVisible = !(
    companyDetails.documentType === DocumentType.identification &&
    companyDetails.selectedCitizenship.value === Citizenship.GE
  )

  const isPassport = companyDetails.documentType === DocumentType.passport
  const isIdentification = companyDetails.documentType === DocumentType.identification

  const handleChangeCitizenship = (item: BaseOption<Citizenship>) => {
    companyDetails.setSelectedCitizenship(item)
    companyDetails.clearPassportStep()
    form.resetFields()
    form.setFieldValue('lastName', profile.lastName)
    form.setFieldValue('firstName', profile.firstName)
    form.setFieldValue('middleName', profile.middleName)

    if (item.value === Citizenship.TJ || item.value === Citizenship.GE) {
      companyDetails.setDocumentType(DocumentType.identification)
      return
    }

    companyDetails.setDocumentType(DocumentType.passport)
  }

  const setDocumentType = (option: BaseOption<DocumentType>) => {
    companyDetails.clearPassportStep()
    companyDetails.setDocumentType(option.value)
    form.resetFields()
    form.setFieldValue('lastName', profile.lastName)
    form.setFieldValue('firstName', profile.firstName)
    form.setFieldValue('middleName', profile.middleName)
  }

  const getDocumentTypeOptions = () => {
    const selectedCitizenship = companyDetails.selectedCitizenship
    return documentsTypeOptions.map((option) => {
      if (option.value === DocumentType.identification) {
        return {
          label: t(getDataKey(identificationCardLabels[selectedCitizenship.value])),
          value: option.value,
        }
      }
      return option
    })
  }

  const localizedSitizenship = useMemo(() => {
    return citizenshipOptions.map((citizenship) => ({
      ...citizenship,
      label: t(getSharedCountriesKey(citizenship.label)),
    }))
  }, [t])

  const localizedCountry = (value: BaseOption<Citizenship>) => {
    return {
      ...value,
      label: t(getSharedCountriesKey(value.label)),
    }
  }

  function getSharedCountriesKey(key: string) {
    return `${UserModuleKeys.countries}.${key}`
  }

  useEffect(() => {
    form.setFieldValue('lastName', profile.lastName)
    form.setFieldValue('firstName', profile.firstName)
    form.setFieldValue('middleName', profile.middleName)
    form.setFieldValue('passport', companyDetails.passport)
    form.setFieldValue('nationality', companyDetails.nationality)
    form.setFieldValue('identificationNumber', companyDetails.identificationNumber)
    form.setFieldValue('passportIssued', companyDetails.passportIssued)
    form.setFieldValue('registrationAddress', companyDetails.registrationAddress)
    form.setFieldValue('departmentCode', companyDetails.departmentCode)
    form.setFieldValue('placeOfBirth', companyDetails.placeOfBirth)
    form.setFieldValue('documentNumber', companyDetails.documentNumber)
    form.setFieldValue('iin', companyDetails.iin)
    form.setFieldValue('socialCardNumber', companyDetails.socialCardNumber)
    form.setFieldValue('personalNumber', companyDetails.personalNumber)
    form.setFieldValue('innCitizen', companyDetails.innCitizen)
    form.setFieldValue(
      'dateOfIssue',
      companyDetails.dateOfIssue ? dayjs(companyDetails.dateOfIssue, DATE_FORMAT_DEFAULT) : null,
    )
    form.setFieldValue(
      'expirationDate',
      companyDetails.expirationDate ? dayjs(companyDetails.expirationDate, DATE_FORMAT_DEFAULT) : null,
    )
    form.setFieldValue(
      'dateOfBirthday',
      companyDetails.dateOfBirthday ? dayjs(companyDetails.dateOfBirthday, DATE_FORMAT_DEFAULT) : null,
    )
  }, [
    profile.lastName,
    profile.firstName,
    profile.middleName,
    companyDetails.passport,
    companyDetails.nationality,
    companyDetails.identificationNumber,
    companyDetails.dateOfIssue,
    companyDetails.expirationDate,
    companyDetails.dateOfBirthday,
    companyDetails.passportIssued,
    companyDetails.registrationAddress,
    companyDetails.departmentCode,
    companyDetails.placeOfBirth,
    companyDetails.documentNumber,
    companyDetails.iin,
    companyDetails.socialCardNumber,
    companyDetails.personalNumber,
    companyDetails.innCitizen,
  ])

  return (
    <Form form={form} layout="vertical" style={{ maxWidth: 600, marginTop: 50 }}>
      <FormItem label={t(getKey('Гражданство'))}>
        <ReactSelect
          isDisabled={isEdit}
          placeholder={t(getKey('Выберите гражданство'))}
          options={localizedSitizenship}
          value={localizedCountry(
            citizenshipOptions.find((option) => option.value === companyDetails.selectedCitizenship.value) ??
              citizenshipOptions[0],
          )}
          onChange={(citizenship) => handleChangeCitizenship(citizenship as BaseOption<Citizenship>)}
        />
      </FormItem>

      {isCountryWithMultipleDocuments && (
        <FormItem label={t(getKey('Тип документа'))}>
          <ReactSelect
            isDisabled={isEdit}
            placeholder={t(getKey('Выберите тип документа'))}
            value={getDocumentTypeOptions().find((option) => option.value === companyDetails.documentType)}
            options={getDocumentTypeOptions()}
            onChange={(option) => setDocumentType(option as BaseOption<DocumentType>)}
          />
        </FormItem>
      )}

      <FormItem
        label={t(getKey('Фамилия'))}
        name={'lastName'}
        rules={[{ required: true, message: t(getSharedKey('required')) }]}
        normalize={(value) => value.replace(NOT_LATIN_CYRILLIC_HYPHEN, '')}
      >
        <Input
          value={profile.lastName}
          onChange={(e) => profile.setLastName(e.target.value)}
          placeholder={fioPlaceholders[companyDetails.selectedCitizenship.value].lastName}
        />
      </FormItem>
      <FormItem
        label={t(getKey('Имя'))}
        name={'firstName'}
        rules={[{ required: true, message: t(getSharedKey('required')) }]}
        normalize={(value) => value.replace(NOT_LATIN_CYRILLIC_HYPHEN, '')}
      >
        <Input
          value={profile.firstName}
          onChange={(e) => profile.setFirstName(e.target.value)}
          placeholder={fioPlaceholders[companyDetails.selectedCitizenship.value].firstName}
        />
      </FormItem>

      {isMiddleNameVisible && (
        <FormItem
          label={t(getKey('Отчество (при наличии)'))}
          name={'middleName'}
          normalize={(value) => value.replace(NOT_LATIN_CYRILLIC_HYPHEN, '')}
        >
          <Input
            value={profile.middleName}
            onChange={(e) => profile.setMiddleName(e.target.value)}
            placeholder={fioPlaceholders[companyDetails.selectedCitizenship.value].middleName}
          />
        </FormItem>
      )}

      {isPassport && <PassportForm uiStore={companyDetails} />}

      {isIdentification && <IdentificationForm uiStore={companyDetails} />}

      <Divider />
    </Form>
  )
})
